import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import axios from "axios";
import { captureException } from "@sentry/react";
import { toast } from "react-toastify";
import {
  getDataset,
  getDatasetImages,
  getImageAnnotations,
  type DatasetImage,
  type ImageAnnotation,
  type DatasetKind,
  DATASET_API,
  PAGE_SIZE,
} from "../api";
import t from "../translations";

interface DatasetDetailContextValue {
  datasetId: number;
  kind: DatasetKind;
  detailBasePath: string;
  datasetName: string;
  // Object type ids included in this dataset (DatasetMeta.type_list).
  datasetTypeList: number[];
  totalImageCount: number;
  projectCounts: { id: number; count: number }[];
  images: DatasetImage[];
  selectedImage: DatasetImage | null;
  annotations: ImageAnnotation[];
  loading: boolean;
  hasMore: boolean;
  loadMore: () => void;
}

const DatasetDetailContext = createContext<DatasetDetailContextValue | null>(
  null
);

export function DatasetDetailProvider({
  datasetId,
  imageId,
  kind = "object",
  children,
}: {
  datasetId: number;
  imageId: number;
  kind?: DatasetKind;
  children: ReactNode;
}) {
  const [datasetName, setDatasetName] = useState("");
  const [datasetTypeList, setDatasetTypeList] = useState<number[]>([]);
  const [totalImageCount, setTotalImageCount] = useState(0);
  const [projectCounts, setProjectCounts] = useState<
    { id: number; count: number }[]
  >([]);
  const [images, setImages] = useState<DatasetImage[]>([]);
  const [annotations, setAnnotations] = useState<ImageAnnotation[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const imagesControllerRef = useRef<AbortController | null>(null);
  const annotationsControllerRef = useRef<AbortController | null>(null);

  const selectedImage = useMemo(
    () => images.find((img) => img.id === imageId) ?? null,
    [images, imageId]
  );

  const api = DATASET_API[kind];

  const loadDataset = useCallback(async () => {
    try {
      const dataset = await api.detail(datasetId);
      setDatasetName(dataset.name);
      setDatasetTypeList(dataset.type_list);
      setTotalImageCount(dataset.image_count);
      setProjectCounts(
        dataset.project_counts.map(({ project_id, count }) => ({
          id: project_id,
          count,
        }))
      );
    } catch (error) {
      if (!axios.isCancel(error)) {
        captureException(error, { event_id: "datasetDetail.fetch" });
        toast.error(t.FailedToLoadDataset);
      }
    }
  }, [api, datasetId]);

  const loadImages = useCallback(
    async (lastId: number) => {
      imagesControllerRef.current?.abort();
      const controller = new AbortController();
      imagesControllerRef.current = controller;

      setLoading(true);
      try {
        const data = await api.images(datasetId, lastId, controller.signal);

        setImages((prev) => (lastId === 0 ? data : [...prev, ...data]));
        setHasMore(data.length === PAGE_SIZE);
      } catch (error) {
        if (!axios.isCancel(error)) {
          captureException(error, { event_id: "datasetDetail.fetch" });
          toast.error(t.FailedToLoadImages);
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [api, datasetId]
  );

  useEffect(() => {
    if (!datasetId) return;

    loadDataset();
    loadImages(0);

    return () => {
      imagesControllerRef.current?.abort();
    };
  }, [datasetId, loadDataset, loadImages]);

  useEffect(() => {
    annotationsControllerRef.current?.abort();
    setAnnotations([]);
    if (!selectedImage) return;

    const controller = new AbortController();
    annotationsControllerRef.current = controller;

    (async () => {
      try {
        const data = await getImageAnnotations(
          selectedImage.region,
          selectedImage.project_id,
          selectedImage.id,
          controller.signal
        );
        setAnnotations(data);
      } catch (error) {
        if (!axios.isCancel(error)) {
          captureException(error, { event_id: "datasetDetail.fetch" });
          toast.error(t.FailedToLoadAnnotations);
        }
      }
    })();

    return () => controller.abort();
  }, [selectedImage]);

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    const lastId = images[images.length - 1]?.id ?? 0;
    loadImages(lastId);
  }, [loading, hasMore, images, loadImages]);

  return (
    <DatasetDetailContext.Provider
      value={{
        datasetId,
        kind,
        detailBasePath: api.basePath,
        datasetName,
        datasetTypeList,
        totalImageCount,
        projectCounts,
        images,
        selectedImage,
        annotations,
        loading,
        hasMore,
        loadMore,
      }}
    >
      {children}
    </DatasetDetailContext.Provider>
  );
}

export function useDatasetDetail(): DatasetDetailContextValue {
  const ctx = useContext(DatasetDetailContext);
  if (!ctx) {
    throw new Error(
      "useDatasetDetail must be used within DatasetDetailProvider"
    );
  }
  return ctx;
}
